import type {
  ActivityStatus,
  ControlPlaneConnectionState,
  ControlPlaneExpiryBand,
  OnboardingAttemptState,
  OnboardingBootstrapActionCode,
  OnboardingBootstrapStage,
  SessionEvidenceState,
  SessionIntegrationState,
  SessionObservabilityActionCode,
  SessionObservabilityCapability,
  SessionObservabilityProjection,
  SessionSignalCondition,
  WorkspaceCleanupState,
} from '@internal/spassu-code-contract';
import type { CockpitSessionObservabilityView } from '@internal/spassu-code-cockpit-ui';

export const ATTEMPT_STATE_LABELS: Record<OnboardingAttemptState, string> = {
  in_progress: 'Em preparação',
  succeeded: 'Concluída',
  failed: 'Bootstrap não concluído',
};

export const BOOTSTRAP_STAGE_LABELS: Record<OnboardingBootstrapStage, string> =
  {
    redeemed: 'Autorização resgatada',
    workspace_ready: 'Workspace preparado',
    bundle_ready: 'Pacote validado',
    local_state_ready: 'Estado local gravado',
    committed: 'Conexão confirmada',
    failed: 'Etapa não concluída',
  };

export const BOOTSTRAP_ACTION_LABELS: Record<
  OnboardingBootstrapActionCode,
  string
> = {
  resume_same_workspace: 'Retome no mesmo workspace.',
  generate_new_code: 'Gere um novo código de conexão.',
  inspect_workspace: 'Verifique o workspace antes de tentar novamente.',
  contact_support: 'Entre em contato com o suporte.',
};

export const SIGNAL_HEALTH_LABELS: Record<SessionSignalCondition, string> = {
  accepted: 'Sinais aceitos',
  rejected: 'Sinais rejeitados',
  not_observed: 'Sinais não observados',
  stale: 'Leitura desatualizada',
};

export const ACTIVITY_CYCLE_LABELS: Record<ActivityStatus, string> = {
  preparing: 'Preparando',
  active: 'Ativa',
  completed: 'Concluída',
  cancelled: 'Cancelada',
  error: 'Erro',
};

export const CONNECTION_LABELS: Record<ControlPlaneConnectionState, string> = {
  not_started: 'Não iniciada',
  awaiting_pairing: 'Aguardando pareamento',
  connecting: 'Conectando',
  connected: 'Conectada',
  expired: 'Expirada',
  revoked: 'Revogada',
  failed: 'Falhou',
  unavailable: 'Indisponível',
};

export const EXPIRY_BAND_LABELS: Record<ControlPlaneExpiryBand, string> = {
  none: 'Sem aviso de validade',
  two_hours: 'Expira em menos de 2 horas',
  thirty_minutes: 'Expira em menos de 30 minutos',
  expired: 'Validade expirada',
};

export const WORKSPACE_LABELS: Record<WorkspaceCleanupState, string> = {
  not_requested: 'Nenhuma exclusão pendente',
  requested: 'Exclusão local pendente',
  claimed: 'Exclusão local em andamento',
  retryable_failed: 'Exclusão local pendente (nova tentativa)',
  confirmed: 'Workspace removido',
  overridden: 'Exclusão local ignorada por administrador',
};

export const SESSION_INTEGRATION_LABELS: Record<
  SessionIntegrationState,
  string
> = {
  integrated: 'Integrada',
  degraded: 'Degradada',
  blocked: 'Bloqueada',
};

export const SESSION_CAPABILITY_LABELS: Record<
  SessionObservabilityCapability,
  string
> = {
  binding: 'Vinculação da sessão',
  mcp: 'MCP',
  'skill-vault': 'Catálogo de skills',
  'skill-lifecycle': 'Ciclo de vida das skills',
  usage: 'Métricas de uso',
  finalization: 'Encerramento e flush',
};

export const SESSION_EVIDENCE_LABELS: Record<SessionEvidenceState, string> = {
  confirmed: 'Confirmada',
  pending: 'Pendente',
  rejected: 'Rejeitada',
  unavailable: 'Indisponível',
  unsupported: 'Não suportada',
};

export const SESSION_ACTION_LABELS: Record<
  SessionObservabilityActionCode,
  string
> = {
  none: 'Nenhuma ação necessária.',
  retry: 'Tente novamente pelos controles autorizados.',
  reconnect: 'Refaça a conexão segura da sessão.',
  repair: 'Repare a integração antes de continuar.',
  'upgrade-runtime': 'Atualize o runtime e refaça a conexão segura.',
};

export function createSessionObservabilityView(input: {
  projection: SessionObservabilityProjection;
  stale: boolean;
  skillsMissing?: boolean;
  usageMissing?: boolean;
}): CockpitSessionObservabilityView {
  const { projection } = input;
  return {
    runtimeLabel: projection.runtimeKind === 'claude' ? 'Claude' : 'Codex',
    state: projection.state,
    stateLabel: SESSION_INTEGRATION_LABELS[projection.state],
    actionCode: projection.actionCode,
    actionLabel: SESSION_ACTION_LABELS[projection.actionCode],
    observedAt: projection.observedAt,
    stale: input.stale,
    capabilities: projection.capabilities.map(evidence => ({
      ...evidence,
      label: SESSION_CAPABILITY_LABELS[evidence.capability],
      stateLabel: SESSION_EVIDENCE_LABELS[evidence.state],
    })),
    jointDiagnostic: jointDiagnostic(input),
  };
}

function jointDiagnostic(input: {
  projection: SessionObservabilityProjection;
  skillsMissing?: boolean;
  usageMissing?: boolean;
}): CockpitSessionObservabilityView['jointDiagnostic'] {
  if (
    input.projection.state === 'integrated' ||
    !input.skillsMissing ||
    !input.usageMissing
  ) {
    return null;
  }
  const skills = input.projection.capabilities.find(
    item => item.capability === 'skill-lifecycle',
  );
  const usage = input.projection.capabilities.find(
    item => item.capability === 'usage',
  );
  if (skills?.state === 'confirmed' || usage?.state === 'confirmed') {
    return null;
  }
  return {
    summary:
      'Skills e métricas estão ausentes pela mesma degradação da integração da sessão.',
    impact:
      'O Cockpit não pode confirmar execução de skills nem consumo de tokens nesta leitura.',
    action: SESSION_ACTION_LABELS[input.projection.actionCode],
  };
}

/** Absolute instant rendered in the viewer's locale, never a derived band. */
export function formatManagementInstant(value: string | null): string | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return null;
  return new Date(parsed).toLocaleString();
}
